import { useEffect, useState } from "react";
import { createStudent, fetchStudents } from "@/api/trainerAuth";

export default function CreateStudent() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });

  const [students, setStudents] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadStudents = async () => {
    try {
      const data = await fetchStudents();
      setStudents(data);
    } catch {
      setError("فشل تحميل الطلاب");
    }
  };

  useEffect(() => {
    loadStudents();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await createStudent(form);

      setSuccess("تم إنشاء الطالب بنجاح");
      setForm({
        name: "",
        email: "",
        password: "",
        phone: "",
      });

      loadStudents(); // إعادة تحميل القائمة
    } catch {
      setError("فشل إنشاء الطالب");
    } finally {
      setLoading(false);
    }
  };

  // تصفية الطلاب حسب البحث
  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.phone?.includes(searchTerm)
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>إدارة الطلاب</h1>
        <p style={styles.pageSubtitle}>أضف طلاباً جدداً وأدر قائمتك الحالية</p>
      </div>

      <div style={styles.content}>
        {/* نموذج إضافة طالب */}
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>إنشاء طالب جديد</h2>
          
          {success && (
            <div style={styles.successMessage}>
              <span style={styles.successIcon}>✅</span>
              {success}
            </div>
          )}
          
          {error && (
            <div style={styles.errorMessage}>
              <span style={styles.errorIcon}>⚠️</span>
              {error}
            </div>
          )}

          <form onSubmit={submit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> اسم الطالب
                </label>
                <input
                  placeholder="أدخل الاسم الكامل للطالب"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> البريد الإلكتروني
                </label>
                <input
                  type="email"
                  placeholder="example@student.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  style={styles.input}
                  dir="ltr"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <span style={styles.required}>*</span> كلمة المرور
                </label>
                <input
                  type="password"
                  placeholder="أدخل كلمة مرور قوية"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  style={styles.input}
                />
                <small style={styles.helpText}>يجب أن تكون 8 أحرف على الأقل</small>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>رقم الهاتف</label>
                <input
                  placeholder="05xxxxxxxx"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  style={styles.input}
                  dir="ltr"
                />
                <small style={styles.helpText}>اختياري</small>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              style={loading ? {...styles.submitButton, ...styles.submitButtonLoading} : styles.submitButton}
            >
              {loading ? (
                <>
                  <span style={styles.spinner}></span>
                  جاري الإنشاء...
                </>
              ) : (
                "إنشاء الطالب"
              )}
            </button>
          </form>
        </div>

        {/* قائمة الطلاب */}
        <div style={styles.listCard}>
          <div style={styles.listHeader}>
            <h2 style={styles.listTitle}>قائمة الطلاب</h2>
            <div style={styles.listStats}>
              <span style={styles.stat}>
                <strong>{students.length}</strong> طالب إجمالي
              </span>
              <span style={styles.stat}>
                <strong>{filteredStudents.length}</strong> طالب بعد التصفية
              </span>
            </div>
          </div>

          <div style={styles.searchBox}>
            <input
              type="text"
              placeholder="🔍 ابحث عن طالب بالاسم أو البريد أو الهاتف..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm("")}
                style={styles.clearSearch}
              >
                ✕
              </button>
            )}
          </div>

          {filteredStudents.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>👨‍🎓</div>
              <h3 style={styles.emptyTitle}>
                {searchTerm ? "لا توجد نتائج بحث" : "لا يوجد طلاب بعد"}
              </h3>
              <p style={styles.emptyText}>
                {searchTerm 
                  ? "جرب مصطلحات بحث مختلفة" 
                  : "ابدأ بإضافة أول طالب لك"}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>اسم الطالب</th>
                      <th style={styles.th}>البريد الإلكتروني</th>
                      <th style={styles.th}>رقم الهاتف</th>
                      <th style={styles.th}>الحالة</th>
                      <th style={styles.th}>تاريخ الإضافة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student, index) => (
                      <tr key={student.id} style={styles.tr}>
                        <td style={styles.td}>{index + 1}</td>
                        <td style={styles.td}>
                          <div style={styles.studentCell}>
                            <div style={styles.avatar}>
                              {student.name?.charAt(0) || "ط"}
                            </div>
                            <div>
                              <strong style={styles.studentName}>{student.name}</strong>
                              <small style={styles.studentId}>ID: {student.id}</small>
                            </div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <a href={`mailto:${student.email}`} style={styles.emailLink}>
                            {student.email}
                          </a>
                        </td>
                        <td style={styles.td}>
                          {student.phone ? (
                            <a href={`tel:${student.phone}`} style={styles.phoneLink}>
                              {student.phone}
                            </a>
                          ) : (
                            <span style={styles.notAvailable}>غير متوفر</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.statusBadge,
                            ...(student.is_active ? styles.statusActive : styles.statusInactive)
                          }}>
                            {student.is_active ? "نشط" : "غير نشط"}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {student.created_at 
                            ? new Date(student.created_at).toLocaleDateString('ar-SA')
                            : "غير معروف"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div style={styles.tableFooter}>
                <div style={styles.pagination}>
                  <button style={styles.pageButton} disabled>‹</button>
                  <span style={styles.pageInfo}>الصفحة 1 من 1</span>
                  <button style={styles.pageButton} disabled>›</button>
                </div>
                <div style={styles.exportButtons}>
                  <button style={styles.exportButton}>
                    📥 تصدير البيانات
                  </button>
                  <button style={styles.printButton}>
                    🖨️ طباعة القائمة
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: '#f5f7fa',
    minHeight: '100vh',
  },
  header: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #1a2530 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '0 0 15px 15px',
    marginBottom: '30px',
  },
  pageTitle: {
    fontSize: '2rem',
    margin: '0 0 10px 0',
    fontWeight: '700',
  },
  pageSubtitle: {
    fontSize: '1rem',
    opacity: '0.9',
    margin: 0,
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px 40px',
  },
  formCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '30px',
    marginBottom: '30px',
    animation: 'fadeIn 0.5s ease-out',
  },
  formTitle: {
    fontSize: '1.5rem',
    margin: '0 0 20px 0',
    color: '#333',
    fontWeight: '600',
  },
  successMessage: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #4CAF50',
    animation: 'fadeIn 0.3s ease-out',
  },
  successIcon: {
    fontSize: '1.2rem',
  },
  errorMessage: {
    background: '#ffebee',
    color: '#c62828',
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #f44336',
    animation: 'fadeIn 0.3s ease-out',
  },
  errorIcon: {
    fontSize: '1.2rem',
  },
  form: {
    marginTop: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  inputGroup: {
    marginBottom: '10px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#555',
    fontSize: '0.95rem',
  },
  required: {
    color: '#f44336',
    marginLeft: '4px',
  },
  input: {
    width: '100%',
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  helpText: {
    display: 'block',
    marginTop: '6px',
    color: '#777',
    fontSize: '0.85rem',
  },
  submitButton: {
    padding: '14px 30px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  submitButtonLoading: {
    opacity: '0.8',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '18px',
    height: '18px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 1s ease-in-out infinite',
  },
  listCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '30px',
    animation: 'fadeIn 0.5s ease-out 0.2s',
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
  },
  listTitle: {
    fontSize: '1.5rem',
    margin: 0,
    color: '#333',
    fontWeight: '600',
  },
  listStats: {
    display: 'flex',
    gap: '20px',
  },
  stat: {
    background: '#f5f7fa',
    padding: '8px 15px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    color: '#666',
  },
  searchBox: {
    position: 'relative',
    marginBottom: '25px',
  },
  searchInput: {
    width: '100%',
    padding: '12px 45px 12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  },
  clearSearch: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#f5f5f5',
    border: 'none',
    color: '#666',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#777',
  },
  emptyIcon: {
    fontSize: '4rem',
    opacity: '0.5',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '1.5rem',
    margin: '0 0 10px 0',
    color: '#555',
  },
  emptyText: {
    fontSize: '1rem',
    opacity: '0.8',
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '8px',
    border: '1px solid #f0f0f0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px',
  },
  th: {
    padding: '15px',
    textAlign: 'right',
    background: '#f8f9fa',
    color: '#555',
    fontWeight: '600',
    fontSize: '0.9rem',
    borderBottom: '2px solid #e0e0e0',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.3s ease',
  },
  td: {
    padding: '15px',
    textAlign: 'right',
    fontSize: '0.9rem',
    color: '#333',
  },
  studentCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  studentName: {
    display: 'block',
    marginBottom: '3px',
  },
  studentId: {
    fontSize: '0.8rem',
    color: '#666',
  },
  emailLink: {
    color: '#667eea',
    textDecoration: 'none',
    direction: 'ltr',
    display: 'block',
  },
  phoneLink: {
    color: '#4CAF50',
    textDecoration: 'none',
    direction: 'ltr',
    display: 'block',
  },
  notAvailable: {
    color: '#999',
    fontStyle: 'italic',
  },
  statusBadge: {
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  statusActive: {
    background: '#e8f5e9',
    color: '#2e7d32',
  },
  statusInactive: {
    background: '#ffebee',
    color: '#c62828',
  },
  tableFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '25px',
    paddingTop: '20px',
    borderTop: '1px solid #f0f0f0',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  pageButton: {
    padding: '8px 12px',
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#333',
    transition: 'all 0.3s ease',
  },
  pageInfo: {
    fontSize: '0.9rem',
    color: '#666',
  },
  exportButtons: {
    display: 'flex',
    gap: '10px',
  },
  exportButton: {
    padding: '10px 20px',
    background: '#f5f7fa',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#333',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  printButton: {
    padding: '10px 20px',
    background: '#f5f7fa',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#333',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      .input-focus:focus {
        outline: none;
        border-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
      }
      
      .submit-btn-hover:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3) !important;
      }
      
      .table-row-hover:hover {
        background: #f8f9ff !important;
      }
      
      .email-link-hover:hover {
        text-decoration: underline !important;
        color: #764ba2 !important;
      }
      
      .phone-link-hover:hover {
        text-decoration: underline !important;
        color: #2E7D32 !important;
      }
      
      .export-btn-hover:hover {
        background: #667eea !important;
        color: white !important;
        border-color: #667eea !important;
      }
      
      .print-btn-hover:hover {
        background: #4CAF50 !important;
        color: white !important;
        border-color: #4CAF50 !important;
      }
      
      .page-btn-hover:hover:not(:disabled) {
        background: #667eea !important;
        color: white !important;
        border-color: #667eea !important;
      }
      
      .clear-search-hover:hover {
        background: #ffebee !important;
        color: #f44336 !important;
      }
      
      .avatar-hover:hover {
        transform: scale(1.1);
      }
      
      @media (max-width: 768px) {
        .list-header-responsive {
          flex-direction: column !important;
          gap: 15px !important;
          align-items: flex-start !important;
        }
        
        .table-footer-responsive {
          flex-direction: column !important;
          gap: 20px !important;
          align-items: flex-start !important;
        }
        
        .export-buttons-responsive {
          flex-wrap: wrap !important;
        }
      }
      
      @media (max-width: 480px) {
        .form-grid-responsive {
          grid-template-columns: 1fr !important;
        }
        
        .list-stats-responsive {
          flex-direction: column !important;
          gap: 10px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // إضافة classes للعناصر
    setTimeout(() => {
      document.querySelectorAll('input').forEach(el => {
        el.className = (el.className || '') + ' input-focus';
      });
      document.querySelectorAll('[style*="submitButton"]').forEach(el => {
        el.className = (el.className || '') + ' submit-btn-hover';
      });
      document.querySelectorAll('[style*="tr"]').forEach(el => {
        el.className = (el.className || '') + ' table-row-hover';
      });
      document.querySelectorAll('[style*="emailLink"]').forEach(el => {
        el.className = (el.className || '') + ' email-link-hover';
      });
      document.querySelectorAll('[style*="phoneLink"]').forEach(el => {
        el.className = (el.className || '') + ' phone-link-hover';
      });
      document.querySelectorAll('[style*="exportButton"]').forEach(el => {
        el.className = (el.className || '') + ' export-btn-hover';
      });
      document.querySelectorAll('[style*="printButton"]').forEach(el => {
        el.className = (el.className || '') + ' print-btn-hover';
      });
      document.querySelectorAll('[style*="pageButton"]').forEach(el => {
        el.className = (el.className || '') + ' page-btn-hover';
      });
      document.querySelectorAll('[style*="clearSearch"]').forEach(el => {
        el.className = (el.className || '') + ' clear-search-hover';
      });
      document.querySelectorAll('[style*="avatar"]').forEach(el => {
        el.className = (el.className || '') + ' avatar-hover';
      });
      
      // Responsive classes
      if (window.innerWidth <= 768) {
        const listHeader = document.querySelector('[style*="listHeader"]');
        const tableFooter = document.querySelector('[style*="tableFooter"]');
        const exportButtons = document.querySelector('[style*="exportButtons"]');
        if (listHeader) listHeader.className = (listHeader.className || '') + ' list-header-responsive';
        if (tableFooter) tableFooter.className = (tableFooter.className || '') + ' table-footer-responsive';
        if (exportButtons) exportButtons.className = (exportButtons.className || '') + ' export-buttons-responsive';
      }
      if (window.innerWidth <= 480) {
        const formGrid = document.querySelector('[style*="formGrid"]');
        const listStats = document.querySelector('[style*="listStats"]');
        if (formGrid) formGrid.className = (formGrid.className || '') + ' form-grid-responsive';
        if (listStats) listStats.className = (listStats.className || '') + ' list-stats-responsive';
      }
    }, 200);
  }, 100);
}